import React, { useState } from 'react';
import { useNavigate, useLocation, useSearchParams, Link } from 'react-router-dom';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CompanyBrandMark from '../../components/layout/CompanyBrandMark';

const EmployeeLogin = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Success message passed from password setup/reset redirection
  const successBanner = location.state?.successMessage || searchParams.get('message');

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!username || !password) {
      setError('Please enter your Employee Code / Username and password');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch('/api/employee-auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password })
      });

      const res = await response.json();

      if (res.success && res.data?.token) {
        localStorage.setItem('employeeToken', res.data.token);
        localStorage.setItem('employeeUser', JSON.stringify(res.data.employee));
        window.dispatchEvent(new Event('authChange'));
        navigate('/employee/portal/attendance');
      } else {
        setError(res.error || res.message || 'Invalid login credentials.');
      }
    } catch (err) {
      setError(err.message || 'Error connecting to login server.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-muted/40 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="sm:mx-auto sm:w-full sm:max-w-md text-center mb-6">
        <Link to="/" className="inline-block mb-4">
          <CompanyBrandMark size="lg" />
        </Link>
        <h2 className="text-2xl font-bold tracking-tight text-foreground">
          Employee Self-Service
        </h2>
        <p className="mt-1 text-xs text-muted-foreground">
          Log in to access your attendance, leaves, payslips, and profile
        </p>
      </div>

      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-card border border-border py-8 px-6 shadow-xl rounded-xl sm:px-10">
          
          {/* Success Banner */}
          {successBanner && (
            <div className="mb-5 p-3.5 bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 rounded-lg text-xs font-semibold flex items-start gap-2.5">
              <Icon name="CheckCircle2" size={18} className="shrink-0 mt-0.5" />
              <span>{successBanner}</span>
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit}>
            {error && (
              <div className="p-3 bg-rose-500/10 border border-rose-500/20 text-rose-600 rounded-lg text-xs font-semibold flex items-center gap-2">
                <Icon name="AlertCircle" size={16} className="shrink-0" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Employee Code / Username
              </label>
              <Input
                type="text"
                placeholder="e.g. EMP-0003"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                icon="User"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-foreground mb-1.5">
                Password
              </label>
              <Input
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                icon="Lock"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-2.5 shadow-sm"
              iconName={loading ? "Loader2" : "LogIn"}
              iconPosition="left"
            >
              {loading ? "Authenticating..." : "Sign In to Portal"}
            </Button>
          </form>

          {/* First time setup link */}
          <div className="mt-4 text-center">
            <Link
              to="/employee/setup-password"
              className="text-xs font-semibold text-teal-600 hover:underline inline-flex items-center gap-1"
            >
              <Icon name="UserPlus" size={14} /> First time logging in? Set up your password
            </Link>
          </div>

          <div className="mt-6 pt-4 border-t border-border text-center space-y-1.5">
            <p className="text-xs text-muted-foreground">
              Forgot password?{' '}
              <Link
                to="/employee/setup-password?mode=forgot"
                className="font-semibold text-teal-600 hover:underline"
              >
                Reset Password
              </Link>
            </p>
            <p className="text-[11px] text-muted-foreground">
              If your email on file is outdated or inaccessible, contact HR Department.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmployeeLogin;

